/**
 * HealthSummaryCard - Admin Console Heads-Up Display
 *
 * Task 060a: Provides at-a-glance system health with links to GCP dashboards.
 * Option C implementation: Minimal friction for admins to monitor and operate the platform.
 */

import React from 'react';
import { Card, CardSkeleton, Button } from '@/components/ui';
import type {
  HealthSummary,
  SystemStatus,
} from '@/services/admin/AdminService';

interface HealthSummaryCardProps {
  healthSummary: HealthSummary | null;
  loading: boolean;
  onRefresh?: () => void;
}

// Status indicator colors
const STATUS_STYLES: Record<
  SystemStatus,
  { bg: string; text: string; label: string }
> = {
  operational: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    label: 'Operational',
  },
  degraded: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    label: 'Degraded',
  },
  outage: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    label: 'Outage',
  },
};

// External Link Icon
const ExternalLinkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className || 'w-4 h-4'}
  >
    <path
      fillRule="evenodd"
      d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z"
      clipRule="evenodd"
    />
    <path
      fillRule="evenodd"
      d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z"
      clipRule="evenodd"
    />
  </svg>
);

// Status Badge Component
const StatusBadge: React.FC<{ status: SystemStatus }> = ({ status }) => {
  const style = STATUS_STYLES[status];
  return (
    <span
      className={`px-3 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}
    >
      {style.label}
    </span>
  );
};

// Metric Display Component
const MetricDisplay: React.FC<{
  label: string;
  value: number | string;
  unit?: string;
}> = ({ label, value, unit }) => (
  <div className="text-center">
    <p className="text-2xl font-bold text-secondary-900">
      {value}
      {unit && (
        <span className="text-sm font-normal text-secondary-500 ml-1">
          {unit}
        </span>
      )}
    </p>
    <p className="text-xs text-secondary-600">{label}</p>
  </div>
);

// Dashboard Link Component
const DashboardLink: React.FC<{
  label: string;
  url: string;
  description: string;
}> = ({ label, url, description }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center justify-between p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors group"
  >
    <div>
      <p className="text-sm font-medium text-secondary-900 group-hover:text-primary-600">
        {label}
      </p>
      <p className="text-xs text-secondary-500">{description}</p>
    </div>
    <ExternalLinkIcon className="w-4 h-4 text-secondary-400 group-hover:text-primary-600" />
  </a>
);

export const HealthSummaryCard: React.FC<HealthSummaryCardProps> = ({
  healthSummary,
  loading,
  onRefresh,
}) => {
  if (loading) {
    return <CardSkeleton />;
  }

  if (!healthSummary) {
    return (
      <Card padding="lg">
        <p className="text-secondary-500">Unable to load health summary</p>
      </Card>
    );
  }

  const { status, last_updated, metrics, gcp_dashboard_urls } = healthSummary;

  return (
    <Card padding="lg">
      {/* Header with Status */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-secondary-900">
            System Health
          </h3>
          <StatusBadge status={status} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-secondary-500">
            Last updated: {new Date(last_updated).toLocaleTimeString()}
          </span>
          {onRefresh && (
            <Button variant="ghost" size="sm" onClick={onRefresh}>
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-secondary-50 rounded-lg">
        <MetricDisplay
          label="Active Users (24h)"
          value={metrics.active_users_24h}
        />
        <MetricDisplay
          label="Projects Created (24h)"
          value={metrics.projects_created_24h}
        />
        <MetricDisplay
          label="Agent Invocations (24h)"
          value={metrics.agent_invocations_24h}
        />
        <MetricDisplay
          label="Error Rate (1h)"
          value={metrics.error_rate_1h.toFixed(1)}
          unit="%"
        />
      </div>

      {/* GCP Dashboard Links */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-secondary-700 mb-3">
          Google Cloud Dashboards
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <DashboardLink
            label="Monitoring Dashboard"
            url={gcp_dashboard_urls.main_dashboard}
            description="Full metrics and custom dashboards"
          />
          <DashboardLink
            label="Logs Explorer"
            url={gcp_dashboard_urls.logs}
            description="Search and analyze application logs"
          />
          <DashboardLink
            label="Error Reporting"
            url={gcp_dashboard_urls.errors}
            description="View grouped exceptions and errors"
          />
          <DashboardLink
            label="Trace Explorer"
            url={gcp_dashboard_urls.traces}
            description="Distributed tracing for requests"
          />
        </div>
      </div>
    </Card>
  );
};

export default HealthSummaryCard;
